import { useZero } from "@rocicorp/zero/react";
import { styled } from "goober";
import { useCallback, useEffect, useRef, type ReactNode } from "react";
import { formatModelName } from "../../lib/formatModelName";
import {
  availableEffortLevels,
  defaultChatModel,
  defaultManagedChatModel,
  type EffortLevel,
  getAIProvider,
  isEffortLevel,
  managedAIProviderIdForModel,
  MANAGED_AI_PROVIDER_ID,
  resolveAIProviderId,
  resolveManagedChatModel,
} from "../../../shared/aiProviders";
import { sharedMutators } from "../../../shared/mutators";
import { queries } from "../../../shared/queries";
import { config as environment } from "../../environment";
import { useComposerStrip } from "../../hooks/useComposerStrip";
import { useHasAccess } from "../../hooks/useHasAccess";
import { useQuery } from "../../hooks/useQuery";
import { type ModelScope, useScopedModel } from "../../hooks/useScopedModel";
import { requestChatBrowserNotificationPermission } from "../../lib/browserNotifications";
import { effortLabel } from "../../lib/effort";
import { trackUiEvent } from "../../lib/tracing";
import { makeUrl, routes } from "../../lib/urls";
import { Dropdown } from "../Dropdown";
import { EffortIcon } from "../EffortIcon";
import { ToggleIcon, ToggleIconContainer } from "../HeadingToggle";
import { Icon } from "../Icon";
import { PressButton } from "../PressButton";
import { ProviderLogo } from "../ProviderLogo";
import { MetaSep } from "../MetaSeparator";
import { Text } from "../Text";
import { Tooltip } from "../Tooltip";

const PANEL_HEIGHT = 40;
const CORNER = 12;
// TextareaContainer is the input frame (its inline styles add 4px padding around the
// card); inset the panel by that so it lines up with the input card, not the frame.
const INSET = 4;

const REST = "transparent";
const HOVER = "color-mix(in srgb, var(--tooltip-fg) 10%, transparent)";

// The panel is static: absolutely pinned to the bottom of TextareaContainer, it never
// moves or resizes. Opening only grows the Spacer (in flow), which slides the composer
// up and off the panel to reveal it. CORNER is the composer's bottom radius — the panel
// extends that far up behind it (z-index) so the dark bleeds through its rounded corners.
const Spacer = styled("div")`
  height: 0;
  margin-top: -4px;
  pointer-events: none;
  transition: height 0.12s ease-out;

  &[data-expanded="true"] {
    height: ${PANEL_HEIGHT}px;
  }
`;

const Panel = styled("div")`
  position: absolute;
  left: ${INSET}px;
  right: ${INSET}px;
  bottom: ${INSET}px;
  z-index: -1;
  height: ${PANEL_HEIGHT + CORNER}px;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  border-radius: 0 0 ${CORNER}px ${CORNER}px;
  background: var(--tooltip-bg);
  color: var(--tooltip-fg);
  opacity: 0;
  pointer-events: none;
  transition: opacity 0s linear 0.12s;

  &[data-expanded="true"] {
    opacity: 1;
    pointer-events: auto;
    transition: opacity 0s linear 0s;
  }

  /* The create-workflow input fills its container (no 4px frame like chat's), so
     the panel isn't inset either — it spans the full input width. */
  &[data-inset="false"] {
    left: 0;
    right: 0;
    bottom: 0;
  }
`;

const Content = styled("div")`
  height: ${PANEL_HEIGHT}px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 0 8px;
`;

const PickerButton = styled("button")`
  display: flex;
  align-items: center;
  gap: 6px;
  height: 24px;
  max-width: 240px;
  min-width: 0;
  padding: 0 10px;
  border: none;
  border-radius: 12px;
  background: ${REST};
  color: var(--tooltip-fg);
  cursor: pointer;
  overflow: hidden;

  &:hover:not([data-disabled="true"]) {
    background: ${HOVER};
  }
  &[data-disabled="true"] {
    cursor: default;
    opacity: 0.6;
  }
`;

const Group = styled("div")`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const IconButton = styled(PressButton)`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  flex-shrink: 0;
  border: none;
  border-radius: 50%;
  background: ${REST};
  color: var(--tooltip-fg);
  cursor: pointer;

  &:hover {
    background: ${HOVER};
  }
`;

function Subtitle({ text }: { text: ReactNode }) {
  return (
    <Text variant="text-small" color="var(--text-secondary)" ellipsis>
      {text}
    </Text>
  );
}

export function ComposerStrip({
  onAddFiles,
  modelScope,
  inset = true,
  effort,
  onEffortChange,
  chatModel,
  onChatModelChange,
  deferModelQueries = false,
}: {
  onAddFiles: (files: FileList | File[]) => void;
  modelScope: ModelScope;
  inset?: boolean;
  effort?: EffortLevel | null;
  onEffortChange?: (effort: EffortLevel | null) => void;
  chatModel?: string | null;
  onChatModelChange?: (chatModel: string | null) => void;
  deferModelQueries?: boolean;
}) {
  const { open, close } = useComposerStrip();
  const z = useZero();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const [preferences] = useQuery(queries.userPreferences());
  const {
    managedProvider,
    configs,
    tenantDefaultKey,
    effectiveKey,
    effectiveConfig,
    providerId,
    setModel,
  } = useScopedModel(modelScope, {
    enabled: !deferModelQueries,
    chatModel,
  });
  const soundChat = preferences?.soundChat !== "off";
  const browserNotifications = preferences?.browserNotifications === "on";
  const followUpBehavior =
    preferences?.followUpBehavior === "steer" ? "steer" : "queue";
  const canManageAI = useHasAccess("tenant:ai_providers_manage");

  // Don't persist across navigation: collapse when the chat (and strip) unmounts.
  useEffect(() => () => close(), [close]);

  // Auto-collapse once you stop engaging — i.e. interact outside the composer
  // (input + strip) and outside the open model menu.
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: PointerEvent) => {
      const target = e.target as Element | null;
      const composer = panelRef.current?.parentElement;
      if (composer && target && composer.contains(target)) return;
      if (target?.closest("[data-popover]")) return;
      close();
    };
    document.addEventListener("pointerdown", onPointerDown, true);
    return () =>
      document.removeEventListener("pointerdown", onPointerDown, true);
  }, [open, close]);

  const managedChatModel = managedProvider
    ? resolveManagedChatModel(chatModel)
    : null;
  const chatModelId =
    effectiveConfig?.chatModel ??
    (effectiveKey === MANAGED_AI_PROVIDER_ID && managedChatModel
      ? managedChatModel
      : defaultChatModel(providerId));
  const displayedModel = formatModelName(chatModelId);
  const configEffort = effectiveConfig?.effort;
  const defaultEffort = isEffortLevel(configEffort)
    ? configEffort
    : (getAIProvider(providerId).defaultChatEffort ?? null);
  const availableEfforts = availableEffortLevels(
    getAIProvider(providerId),
    chatModelId
  );
  const resolvedEffort = effort ?? defaultEffort;
  const displayEffort =
    resolvedEffort && availableEfforts.includes(resolvedEffort)
      ? resolvedEffort
      : (availableEfforts[availableEfforts.length - 1] ?? "low");
  const nextEffort =
    availableEfforts[
      (availableEfforts.indexOf(displayEffort) + 1) % availableEfforts.length
    ] ?? "low";
  const effortTooltip = `Effort: ${effortLabel(displayEffort)}${
    effort ? "" : " (default)"
  }`;

  // The Dropdown re-notifies whenever the callback identity changes, so keep
  // it stable to emit one event per open.
  const trackPickerOpen = useCallback(
    (isOpen: boolean) => {
      if (isOpen) {
        trackUiEvent("ui.model-picker-open", { "model.current": chatModelId });
      }
    },
    [chatModelId]
  );

  // Env-managed models aren't tenant configs, so surface each model directly.
  const managedItems = managedProvider
    ? environment.managedChatModels.map((modelId) => {
        const modelProvider = managedAIProviderIdForModel(
          managedProvider,
          modelId
        );
        const isDefault =
          tenantDefaultKey === MANAGED_AI_PROVIDER_ID &&
          modelId === defaultManagedChatModel;
        return {
          key: `${MANAGED_AI_PROVIDER_ID}:${modelId}`,
          type: "button" as const,
          left: <ProviderLogo provider={modelProvider} size={16} />,
          label: formatModelName(modelId),
          subtitle: (
            <Subtitle
              text={
                <>
                  Managed by 3B
                  {isDefault && (
                    <>
                      <MetaSep />
                      Default
                    </>
                  )}
                </>
              }
            />
          ),
          selected:
            effectiveKey === MANAGED_AI_PROVIDER_ID &&
            managedChatModel === modelId,
          right:
            effectiveKey === MANAGED_AI_PROVIDER_ID &&
            managedChatModel === modelId ? (
              <Icon name="check" size={12} color="var(--text-secondary)" />
            ) : undefined,
          onClick: () => {
            onChatModelChange?.(modelId);
            setModel(isDefault ? null : MANAGED_AI_PROVIDER_ID);
          },
        };
      })
    : [];

  const enabledConfigs = configs.filter((config) => config.disabledAt == null);
  const configItems = enabledConfigs.map((config) => {
    const pid = resolveAIProviderId(config);
    const isDefault = tenantDefaultKey === config.id;
    return {
      key: config.id,
      type: "button" as const,
      left: <ProviderLogo provider={pid} size={16} />,
      label: formatModelName(config.chatModel ?? defaultChatModel(pid)),
      subtitle: (
        <Subtitle
          text={
            <>
              {getAIProvider(pid).label}
              {isDefault && (
                <>
                  <MetaSep />
                  Default
                </>
              )}
            </>
          }
        />
      ),
      selected: effectiveKey === config.id,
      right:
        effectiveKey === config.id ? (
          <Icon name="check" size={12} color="var(--text-secondary)" />
        ) : undefined,
      onClick: () => {
        onChatModelChange?.(null);
        setModel(isDefault ? null : config.id);
      },
    };
  });

  const modelItems = [
    ...managedItems,
    ...configItems.sort((a, b) => a.label.localeCompare(b.label)),
  ];
  const items = modelItems.length
    ? modelItems
    : [
        {
          key: "empty",
          type: "button" as const,
          left: null,
          label: "No models configured",
          disabled: true,
          onClick: () => {},
        },
      ];

  const pickerContent = (
    <>
      <ProviderLogo provider={providerId} size={12} onDark />
      <Text variant="text-small-md" color="var(--tooltip-fg)" ellipsis>
        {displayedModel}
      </Text>
      <span style={{ display: "flex", color: "var(--tooltip-fg-muted)" }}>
        <Icon name="chevron-down" size={12} />
      </span>
    </>
  );

  return (
    <>
      <Spacer data-expanded={open} />
      <Panel ref={panelRef} data-expanded={open} data-inset={inset}>
        <Content>
          <Group>
            <Dropdown
              align="top-left"
              minWidth={280}
              onOpenChange={trackPickerOpen}
              opener={
                <PickerButton type="button">{pickerContent}</PickerButton>
              }
              sections={[
                { items },
                ...(canManageAI
                  ? [
                      {
                        items: [
                          {
                            key: "ai-settings",
                            type: "link" as const,
                            left: <Icon name="settings-2" size={16} />,
                            label: "AI settings",
                            subtitle: (
                              <Text
                                variant="text-small"
                                color="var(--text-secondary)"
                                ellipsis
                              >
                                Manage providers and models
                              </Text>
                            ),
                            to: makeUrl(routes.settings.ai),
                          },
                        ],
                      },
                    ]
                  : []),
              ]}
            />
            {onEffortChange && availableEfforts.length > 0 && (
              <Tooltip content={effortTooltip}>
                <IconButton
                  aria-label={effortTooltip}
                  onMouseDown={(e) => e.preventDefault()}
                  onPress={() => onEffortChange(nextEffort)}
                >
                  <EffortIcon level={displayEffort} levels={availableEfforts} />
                </IconButton>
              </Tooltip>
            )}
          </Group>

          <Group>
            <Tooltip
              content={`Follow-up: ${
                followUpBehavior === "steer" ? "Steer" : "Queue"
              }`}
            >
              <IconButton
                aria-label={`Follow-up: ${
                  followUpBehavior === "steer" ? "Steer" : "Queue"
                }`}
                onMouseDown={(e) => e.preventDefault()}
                onPress={() =>
                  z.mutate(
                    sharedMutators.setUserPreference({
                      followUpBehavior:
                        followUpBehavior === "steer" ? "queue" : "steer",
                    })
                  )
                }
              >
                <ToggleIconContainer>
                  <ToggleIcon
                    style={
                      followUpBehavior === "queue"
                        ? { opacity: 1, transform: "rotate(0deg) scale(1)" }
                        : { opacity: 0, transform: "rotate(90deg) scale(0.5)" }
                    }
                  >
                    <Icon name="list-ordered" size={12} />
                  </ToggleIcon>
                  <ToggleIcon
                    style={
                      followUpBehavior === "steer"
                        ? { opacity: 1, transform: "rotate(0deg) scale(1)" }
                        : { opacity: 0, transform: "rotate(-90deg) scale(0.5)" }
                    }
                  >
                    <Icon name="joystick" size={12} />
                  </ToggleIcon>
                </ToggleIconContainer>
              </IconButton>
            </Tooltip>
            <Tooltip
              content={soundChat ? "Mute chat sounds" : "Unmute chat sounds"}
            >
              <IconButton
                onMouseDown={(e) => e.preventDefault()}
                onPress={() =>
                  z.mutate(
                    sharedMutators.setUserPreference({
                      soundChat: soundChat ? "off" : "on",
                    })
                  )
                }
              >
                <Icon name={soundChat ? "volume-2" : "volume-off"} size={16} />
              </IconButton>
            </Tooltip>
            <Tooltip
              content={
                browserNotifications
                  ? "Turn off notifications"
                  : "Turn on notifications"
              }
            >
              <IconButton
                onMouseDown={(e) => e.preventDefault()}
                onPress={async () => {
                  const next = browserNotifications ? "off" : "on";
                  if (next === "on") {
                    const permission =
                      await requestChatBrowserNotificationPermission();
                    if (permission !== "granted") return;
                  }
                  z.mutate(
                    sharedMutators.setUserPreference({
                      browserNotifications: next,
                    })
                  );
                }}
              >
                <Icon
                  name={browserNotifications ? "bell" : "bell-off"}
                  size={16}
                />
              </IconButton>
            </Tooltip>
            <Tooltip content="Add files">
              <IconButton
                onMouseDown={(e) => e.preventDefault()}
                onPress={() => fileInputRef.current?.click()}
              >
                <Icon name="plus" size={16} />
              </IconButton>
            </Tooltip>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              hidden
              onChange={(e) => {
                // Copy to an array before resetting: the reset empties the live
                // FileList, but processFiles reads it later in a state updater.
                const { files } = e.target;
                if (files?.length) onAddFiles(Array.from(files));
                e.target.value = "";
              }}
            />
          </Group>
        </Content>
      </Panel>
    </>
  );
}
